import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../styles/PosPage.css";

import Galon19L from "../assets/Galon19L.jpg";
import Galon15L from "../assets/Galon15L.jpeg";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const products = [
  { id: 1, name: "Galon 19L", price: 6000, priceDelivery: 7000, image: Galon19L },
  { id: 2, name: "Galon 15L", price: 5000, priceDelivery: 6000, image: Galon15L },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const PosPage = () => {
  const [cart, setCart] = useState([]);
  const [transactionsToday, setTransactionsToday] = useState([]);
  const [message, setMessage] = useState("");
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!username || !token) {
      window.location.href = "/login";
    }
  }, [username, token]);

  const fetchTransactionsToday = useCallback(async () => {
    if (!username || !token) return;

    setLoadingTransactions(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/sales/today/${username}?_t=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTransactionsToday(Array.isArray(data) ? data : []);
      } else {
        setTransactionsToday([]);
      }
    } catch (error) {
      console.error("❌ Gagal mengambil transaksi hari ini:", error);
      setTransactionsToday([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [username, token]);

  useEffect(() => {
    fetchTransactionsToday();
  }, [fetchTransactionsToday]);

  const addToCart = (product, isDelivery) => {
    const price = isDelivery ? product.priceDelivery : product.price;
    const existingItemIndex = cart.findIndex(
      (item) => item.name === product.name && item.isDelivery === isDelivery
    );

    if (existingItemIndex !== -1) {
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      const newItem = { ...product, isDelivery, price, quantity: 1 };
      setCart([...cart, newItem]);
    }
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    if (newCart[index].quantity > 1) {
      newCart[index].quantity -= 1;
    } else {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const cartSummary = useMemo(() => {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    return {
      totalItems,
      totalPrice: getTotalPrice(),
    };
  }, [cart]);

  const handleSaveTransaction = async () => {
    if (cart.length === 0) {
      setMessage("⚠️ Keranjang masih kosong.");
      return;
    }

    const transactionData = { username, cart };

    try {
      const response = await fetch(`${API_BASE_URL}/sales`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(transactionData),
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`✅ Berhasil disimpan transaksi ke-${result.total_today} hari ini.`);
        setCart([]);
        fetchTransactionsToday();
      } else {
        setMessage("❌ Gagal menyimpan transaksi.");
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage("❌ Terjadi kesalahan saat menyimpan transaksi.");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/sales/${Number(transactionId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setMessage("🗑️ Transaksi berhasil dihapus.");
        setTransactionsToday((prevTransactions) =>
          prevTransactions.filter((item) => item.transaction_id !== transactionId)
        );
        await fetchTransactionsToday();
      } else {
        setMessage("❌ Gagal menghapus transaksi.");
      }
    } catch (error) {
      console.error("❌ Terjadi kesalahan saat menghapus transaksi:", error);
      setMessage("❌ Terjadi kesalahan saat menghapus transaksi.");
    }
  };

  return (
    <div className="page-shell">
      <div className="page-hero-card">
        <span className="page-hero-badge">Kasir Depot</span>
        <h2 className="page-hero-title">Kasir (POS)</h2>
        <p className="page-hero-subtitle">
          Kelola transaksi galon, pantau keranjang belanja, dan lihat histori transaksi
          hari ini dalam satu halaman yang lebih rapi.
        </p>
      </div>

      <div className="pos-grid-layout">
        <section className="page-card">
          <div className="page-card-header">
            <div>
              <h3 className="section-title">Daftar Produk</h3>
              <p className="section-subtitle">Pilih produk dan jenis layanan yang akan dijual.</p>
            </div>
          </div>

          <div className="pos-product-grid">
            {products.map((product) => (
              <div key={product.id} className="pos-product-card">
                <div className="pos-product-image-wrap">
                  <img src={product.image} alt={product.name} className="pos-product-image" />
                </div>

                <div className="pos-product-body">
                  <h4>{product.name}</h4>
                  <div className="pos-price-stack">
                    <span>Ambil Sendiri: {formatCurrency(product.price)}</span>
                    <span>Diantar: {formatCurrency(product.priceDelivery)}</span>
                  </div>

                  <div className="pos-product-actions">
                    <button onClick={() => addToCart(product, false)}>Tidak Diantar</button>
                    <button className="secondary" onClick={() => addToCart(product, true)}>
                      Diantar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="page-card">
          <div className="page-card-header">
            <div>
              <h3 className="section-title">Keranjang</h3>
              <p className="section-subtitle">Periksa item sebelum transaksi disimpan.</p>
            </div>
          </div>

          <div className="pos-summary-grid">
            <div className="mini-stat-card">
              <span>Total Item</span>
              <strong>{cartSummary.totalItems}</strong>
            </div>
            <div className="mini-stat-card">
              <span>Total Harga</span>
              <strong>{formatCurrency(cartSummary.totalPrice)}</strong>
            </div>
          </div>

          {cart.length === 0 ? (
            <div className="empty-state-box">Keranjang masih kosong.</div>
          ) : (
            <div className="cart-list">
              {cart.map((item, index) => (
                <div key={`${item.id}-${index}`} className="cart-item-card">
                  <div className="cart-item-main">
                    <strong>{item.name}</strong>
                    <span>{item.isDelivery ? "Diantar" : "Tidak Diantar"}</span>
                  </div>
                  <div className="cart-item-meta">
                    <span>
                      {item.quantity} x {formatCurrency(item.price)}
                    </span>
                    <button className="remove-btn" onClick={() => removeFromCart(index)}>
                      Hapus
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pos-cart-footer">
            <div className="pos-total-box">
              <span>Total Bayar</span>
              <strong>{formatCurrency(getTotalPrice())}</strong>
            </div>
            <button className="primary-action-btn" onClick={handleSaveTransaction}>
              Simpan Transaksi
            </button>
          </div>

          {message && <div className="info-message-box">{message}</div>}
        </section>
      </div>

      <section className="page-card">
        <div className="page-card-header">
          <div>
            <h3 className="section-title">Transaksi Hari Ini</h3>
            <p className="section-subtitle">
              Riwayat transaksi terbaru untuk akun <strong>{username}</strong>.
            </p>
          </div>
        </div>

        {loadingTransactions ? (
          <div className="loading-state-box">Memuat transaksi hari ini...</div>
        ) : transactionsToday.length === 0 ? (
          <div className="empty-state-box">Belum ada transaksi hari ini.</div>
        ) : (
          <div className="transaction-grid">
            {transactionsToday.map((item, index) => (
              <div key={item.transaction_id || index} className="transaction-modern-card">
                <div className="transaction-modern-top">
                  <div>
                    <span className="transaction-label">Transaksi</span>
                    <h4>Transaksi ke-{index + 1}</h4>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteTransaction(item.transaction_id)}
                  >
                    Hapus
                  </button>
                </div>

                <div className="transaction-detail-text">{item.details}</div>

                <div className="transaction-modern-bottom">
                  <span>Total</span>
                  <strong>{formatCurrency(item.total_price || 0)}</strong>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PosPage;